import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import type {
  IHqBraintreeSavedContext,
  IHqSavedPaymentMethodNonce,
} from '@/common/interfaces/payment.interface';
import { API_SERVER_URL, V3_APP_BASE_URL } from '@/constants/Config';

interface IBraintreeClientTokenResponse {
  clientToken: string;
  braintreeCustomerId?: string;
}

/**  Endpoint to fetch and post Braintree related work.  */
export const braintreePaypalQuery = createApi({
  reducerPath: 'braintreePaypalQuery',
  baseQuery: axiosBaseQuery({
    baseUrl: API_SERVER_URL,
  }),
  endpoints: (builder) => ({
    getBraintreeClientToken: builder.query<IBraintreeClientTokenResponse, number | undefined>({
      query: (userId?: number) => {
        const queryParams = userId ? `?userId=${encodeURIComponent(userId)}` : '';
        return {
          url: `api/braintree-paypal/client-token${queryParams}`,
          method: 'get',
        };
      },
    }),
    /**
     * Loads saved methods only through v3's session-authenticated endpoint.
     * Used by HQ regular and store checkout; it never accepts a caller-supplied user id.
     */
    getHqBraintreeSavedContext: builder.query<IHqBraintreeSavedContext, void>({
      query: () => ({
        url: `${V3_APP_BASE_URL}api/fp/braintree/saved-context`,
        method: 'get',
      }),
    }),
    /**
     * Mints a single-use nonce server-side for a saved method the browser Vault Manager
     * cannot tokenize (e.g. Google Pay). v3 scopes the token to the session user's customer.
     */
    createHqSavedPaymentMethodNonce: builder.mutation<IHqSavedPaymentMethodNonce, string>({
      query: (paymentMethodToken) => ({
        url: `${V3_APP_BASE_URL}api/fp/braintree/saved-nonce`,
        method: 'post',
        data: { paymentMethodToken },
      }),
    }),
  }),
});

export const {
  useCreateHqSavedPaymentMethodNonceMutation,
  useGetBraintreeClientTokenQuery,
  useGetHqBraintreeSavedContextQuery,
  useLazyGetBraintreeClientTokenQuery,
} = braintreePaypalQuery;
